import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import IndicatorCard from '../components/IndicatorCard';
import { SectionHeading } from '../components/ui';
import { fetchProducts } from '../features/products/productsSlice';
import { translateProduct } from '../utils/productTranslations';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Seo from '../components/Seo';
import { getStaticMeta } from '../seo/pageMeta';
import { UNLISTED_PRODUCT_SLUGS } from '../data/comingSoon';

const controlClass =
  'rounded-btn border border-white/10 bg-surface px-4 py-2.5 text-sm text-body transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const IndicatorsScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { products, loading, error, status } = useSelector((state) => state.products);
  const meta = getStaticMeta('indicators', i18n.language);
  const catalogReady = status === 'succeeded' || status === 'failed';

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [priceSort, setPriceSort] = useState('');

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(products)) {
      setFilteredProducts([]);
      return;
    }
    let filtered = products.filter(
      (indicator) => indicator?.slug && !UNLISTED_PRODUCT_SLUGS.has(indicator.slug)
    );

    if (selectedType) {
      filtered = filtered.filter(
        (indicator) => indicator.category.toLowerCase() === selectedType.toLowerCase()
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (indicator) =>
          indicator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          indicator.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (priceSort) {
      filtered.sort((a, b) =>
        priceSort === 'asc' ? a.price - b.price : b.price - a.price
      );
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedType, priceSort]);

  if (loading) {
    return (
      <>
        <Seo title={meta.title} description={meta.description} path="/indicators" ready={false} />
        <Loader />
      </>
    );
  }
  if (error) {
    return (
      <>
        <Seo title={meta.title} description={meta.description} path="/indicators" ready={catalogReady} />
        <Message variant="error">{error}</Message>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-bg pt-28 pb-24 md:pt-32">
      <Seo title={meta.title} description={meta.description} path="/indicators" ready={catalogReady} />
      <div className="mx-auto max-w-page px-6">
        <SectionHeading
          index={i18n.language === 'fr' ? 'Catalogue' : 'Catalog'}
          title={t('indicators.title')}
          subtitle={t('indicators.subtitle')}
        />

        <div className="mb-12 flex flex-wrap gap-3 border-b border-white/[.06] pb-8">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className={controlClass}
          >
            <option value="">{t('indicators.categories.all')}</option>
            <option value="volume">{t('indicators.categories.volume')}</option>
            <option value="niveaux">{t('indicators.categories.levels')}</option>
            <option value="analyse">{t('indicators.categories.analysis')}</option>
          </select>
          <select
            value={priceSort}
            onChange={(e) => setPriceSort(e.target.value)}
            className={controlClass}
          >
            <option value="">{t('indicators.sort.price')}</option>
            <option value="asc">{t('indicators.sort.price')} ↑</option>
            <option value="desc">{t('indicators.sort.price')} ↓</option>
          </select>
          <div className="relative min-w-[220px] flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('common.search')}
              className={`${controlClass} w-full pl-10 placeholder:text-muted`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((indicator) => {
              const translatedIndicator = translateProduct(indicator, i18n.language);
              return (
                <IndicatorCard
                  key={indicator._id}
                  indicator={{
                    ...translatedIndicator,
                    image:
                      translatedIndicator.image ||
                      translatedIndicator.images?.[0] ||
                      '/images/placeholder.png',
                  }}
                />
              );
            })
          ) : (
            <p className="col-span-full py-16 text-center text-muted">
              {i18n.language === 'fr'
                ? 'Aucun indicateur ne correspond à votre recherche.'
                : 'No indicators match your search.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndicatorsScreen;
